import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FavoriteButton from "@/components/feature/FavoriteButton";
import ShareButton from "@/components/feature/ShareButton";
import EmptyState from "@/components/feature/EmptyState";
import { news } from "@/mocks/news";
import { works } from "@/mocks/works";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = news.find((item) => item.id === id);

  useDocumentMeta(
    article ? `${article.title} - 吴梦梦影视动态` : "资讯未找到 - 吴梦梦影视动态",
    article
      ? article.excerpt
      : "这篇资讯可能已下线或链接有误，请返回资讯列表浏览吴梦梦的最新影视动态。",
    article ? `${article.category},吴梦梦,${article.title}` : "吴梦梦影视资讯",
  );

  if (!article) {
    return (
      <div className="container-content py-10 md:py-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯", to: "/news" }, { label: "未找到" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-file-unknow-line"
            title="没有找到这篇资讯"
            description="链接可能已经变更，或者这篇内容暂时不在列表中。回到资讯页看看最近发生了什么。"
            actionLabel="返回资讯列表"
            onAction={() => navigate("/news")}
          />
        </div>
      </div>
    );
  }

  const relatedWork = article.relatedWorkId
    ? works.find((work) => work.id === article.relatedWorkId)
    : undefined;

  const relatedArticles = news
    .filter((item) => item.id !== article.id)
    .sort((a, b) => {
      const aScore = a.category === article.category ? 1 : 0;
      const bScore = b.category === article.category ? 1 : 0;
      if (aScore !== bScore) return bScore - aScore;
      return b.date.localeCompare(a.date);
    })
    .slice(0, 3);

  const readMinutes = Math.max(2, Math.round(article.body.length * 0.9));

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="container-content py-7 md:py-10">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "资讯", to: "/news" }, { label: article.category }]}
          />

          <div className="mt-5 max-w-4xl">
            <Link
              to={`/news?category=${encodeURIComponent(article.category)}`}
              className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-accent-100 text-accent-900 text-[11px] hover:bg-accent-200 transition-colors"
            >
              <i className="ri-price-tag-3-line text-[10px] leading-none" />
              {article.category}
            </Link>

            <h1 className="mt-4 text-2xl sm:text-3xl md:text-[40px] font-bold leading-[1.3] tracking-tight">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm leading-none" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-sm leading-none" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm leading-none" />
                约 {readMinutes} 分钟阅读
              </span>
              <span className="hidden sm:flex items-center gap-1.5 text-foreground-400">
                <i className="ri-information-line text-sm leading-none" />
                演示内容
              </span>
            </div>
          </div>
        </div>
      </section>

      <article className="container-content py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,760px)_300px] gap-9 lg:gap-12 justify-center">
          <div className="min-w-0">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} 吴梦梦影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="mt-6 text-[15px] leading-[1.95] text-foreground-600 pb-6 border-b border-background-200">
              {article.excerpt}
            </p>

            <div className="mt-6 flex flex-col gap-5">
              {article.body.map((paragraph, index) => (
                <p key={`${article.id}-p-${index}`} className="text-[15px] md:text-base leading-[1.95] text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg bg-primary-50 border border-primary-100 px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-double-quotes-l text-sm leading-none" />
                </span>
                <p className="text-xs md:text-[13px] text-primary-900/80 leading-relaxed">
                  本文为演示示例内容，人物言论、日期与项目信息均为虚构设定，仅用于展示网站的信息组织与阅读体验，不代表任何真实报道。
                </p>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">所属分类</span>
                <Link
                  to={`/news?category=${encodeURIComponent(article.category)}`}
                  className="inline-flex items-center h-7 px-2.5 rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                >
                  {article.category}
                </Link>
              </div>
              <div className="flex items-center gap-2.5">
                <FavoriteButton
                  variant="pill"
                  item={{
                    key: `news:${article.id}`,
                    type: "news",
                    id: article.id,
                    title: article.title,
                    cover: article.cover,
                    meta: `${article.category} · ${article.date}`,
                  }}
                />
                <ShareButton title={`${article.title} - 吴梦梦影视动态`} summary={article.excerpt} />
              </div>
            </div>

            <Link
              to="/news"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-600 transition-colors"
            >
              <i className="ri-arrow-left-line text-base leading-none" />
              返回资讯列表
            </Link>
          </div>

          <aside className="flex flex-col gap-5">
            {relatedWork && (
              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <div className="px-4 py-3.5 border-b border-background-200">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <i className="ri-film-line text-accent-500 leading-none" />
                    相关内容
                  </h2>
                </div>
                <Link to={`/works/${relatedWork.id}`} className="group block">
                  <div className="w-full aspect-[16/10] bg-background-200 overflow-hidden">
                    <img
                      src={relatedWork.cover}
                      alt={`${relatedWork.title} 海报`}
                      title={`${relatedWork.title} 吴梦梦${relatedWork.type}`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="px-4 py-3.5">
                    <p className="text-[10px] font-label tracking-widest text-foreground-400 uppercase">
                      {relatedWork.type} · {relatedWork.year}
                    </p>
                    <h3 className="mt-1.5 text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {relatedWork.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {relatedWork.synopsis}
                    </p>
                  </div>
                </Link>
              </div>
            )}

            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <i className="ri-bookmark-line text-accent-500 leading-none" />
                收藏这篇资讯
              </h2>
              <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                收藏会记录在本机浏览器，最多保存 50 条，随时可以从顶部书签入口找回。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
              <h2 className="text-sm font-semibold">浏览其他分类</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {["影评", "行业新闻", "活动报道", "演员动态"].map((category) => (
                  <Link
                    key={category}
                    to={`/news?category=${encodeURIComponent(category)}`}
                    className="inline-flex items-center h-7 px-2.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:border-accent-400 hover:text-accent-700 transition-colors"
                  >
                    {category}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      <section className="bg-background-100/70 border-t border-background-200">
        <div className="container-content py-10 md:py-14">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
                Keep Reading
              </span>
              <h2 className="mt-2 text-xl md:text-[26px] font-bold">相关文章推荐</h2>
            </div>
            <Link
              to="/news"
              className="shrink-0 inline-flex items-center gap-1 text-xs md:text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
            >
              全部资讯
              <i className="ri-arrow-right-line text-sm leading-none" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {relatedArticles.map((item) => (
              <article key={item.id} className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <Link to={`/news/${item.id}`} className="block">
                  <div className="w-full aspect-[16/10] bg-background-200 overflow-hidden">
                    <img
                      src={item.cover}
                      alt={`${item.title} 配图`}
                      title={`${item.title} 吴梦梦影视资讯`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="px-4 py-4">
                    <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                      <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900">{item.category}</span>
                      <span className="font-label">{item.date}</span>
                    </div>
                    <h3 className="mt-2 text-sm font-semibold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}